import { Vault, type DecodedOp, type Snapshot } from "@envstash/core-wasm";
import { ServerMessage, type Op, type StoredOp } from "@envstash/protocol";
import { api } from "./api.js";
import { backoffDelay } from "./backoff.js";
import { cacheOps, loadCachedOps } from "./db.js";
import { mergeOps } from "./opcache.js";
import { Store } from "./store.js";

export type SyncStatus = "idle" | "loading" | "syncing" | "live" | "offline" | "revoked" | "error";

export interface VaultState {
  ready: boolean;
  status: SyncStatus;
  error: string | null;
  snapshot: Snapshot | null;
  activity: DecodedOp[];
  lastSeq: number;
  head: number;
  lastSyncAt: number | null;
  pendingPushes: number;
}

const EMPTY: VaultState = {
  ready: false,
  status: "idle",
  error: null,
  snapshot: null,
  activity: [],
  lastSeq: 0,
  head: 0,
  lastSyncAt: null,
  pendingPushes: 0,
};

const MAX_ACTIVITY = 3000;
const PING_INTERVAL_MS = 25_000;

/** Holds the unlocked vault, keeps it in sync with the server and exposes a snapshot to React. */
class VaultStore extends Store<VaultState> {
  #vault: Vault | null = null;
  #vaultId = "";
  #deviceId = "";
  #ops: StoredOp[] = [];
  #known = new Set<string>();
  #ws: WebSocket | null = null;
  #attempt = 0;
  #stopped = true;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #pingTimer: ReturnType<typeof setInterval> | null = null;
  #snapshotScheduled = false;
  onRevoked: (() => void) | null = null;

  constructor() {
    super(EMPTY);
  }

  get vault(): Vault | null {
    return this.#vault;
  }

  start(vk: Uint8Array, vaultId: string, deviceId: string): void {
    this.stop();
    this.#stopped = false;
    this.#vault = new Vault(vk, vaultId, deviceId);
    this.#vaultId = vaultId;
    this.#deviceId = deviceId;
    this.#ops = [];
    this.#known = new Set();
    this.set({ ...EMPTY, status: "loading" });
    void this.#initialLoad();
  }

  stop(): void {
    this.#stopped = true;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    if (this.#pingTimer) clearInterval(this.#pingTimer);
    this.#reconnectTimer = null;
    this.#pingTimer = null;
    if (this.#ws) {
      const ws = this.#ws;
      this.#ws = null;
      try {
        ws.close(1000, "stopped");
      } catch {
        // ignore
      }
    }
    if (this.#vault) {
      this.#vault.free();
      this.#vault = null;
    }
    this.set(EMPTY);
  }

  async #initialLoad(): Promise<void> {
    try {
      const cached = await loadCachedOps(this.#vaultId);
      this.#applyStored(cached, false);
      this.set((s) => ({ ...s, ready: true, status: "syncing" }));
      await this.#pullAll();
      this.#connect();
    } catch (e) {
      this.set((s) => ({ ...s, ready: true, status: "error", error: e instanceof Error ? e.message : String(e) }));
      this.#scheduleReconnect();
    }
  }

  async #pullAll(): Promise<void> {
    for (;;) {
      if (this.#stopped) return;
      const from = this.get().lastSeq;
      const page = await api.vault.pull({ from_seq: from, limit: 500 });
      this.#applyStored(page.ops, true);
      this.set((s) => ({ ...s, head: page.head, lastSyncAt: Date.now() }));
      if (!page.more || page.ops.length === 0) return;
    }
  }

  /** Decrypt + apply + (optionally) cache ops from the server. */
  #applyStored(ops: StoredOp[], cache: boolean): void {
    const vault = this.#vault;
    if (!vault || ops.length === 0) return;
    const { all, added } = mergeOps(this.#ops, ops);
    this.#ops = all;
    if (added.length === 0) {
      const maxSeq = Math.max(...ops.map((o) => o.seq));
      if (maxSeq > this.get().lastSeq) this.set((s) => ({ ...s, lastSeq: maxSeq }));
      return;
    }
    const res = vault.applyStored(added);
    for (const op of added) this.#known.add(op.op_id);
    if (cache) void cacheOps(this.#vaultId, added);
    this.set((s) => ({
      ...s,
      lastSeq: Math.max(s.lastSeq, res.last_seq),
      activity: [...res.applied.reverse(), ...s.activity].slice(0, MAX_ACTIVITY),
    }));
    this.#scheduleSnapshot();
  }

  #scheduleSnapshot(): void {
    if (this.#snapshotScheduled) return;
    this.#snapshotScheduled = true;
    queueMicrotask(() => {
      this.#snapshotScheduled = false;
      const vault = this.#vault;
      if (!vault) return;
      this.set((s) => ({ ...s, snapshot: vault.snapshot() }));
    });
  }

  #connect(): void {
    if (this.#stopped || this.#ws) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.#ws = ws;
    ws.onopen = () => {
      this.#attempt = 0;
      ws.send(JSON.stringify({ t: "hello", last_seq: this.get().lastSeq }));
      this.set((s) => ({ ...s, status: "live", error: null }));
      this.#pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "ping" }));
      }, PING_INTERVAL_MS);
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = ServerMessage.parse(JSON.parse(String(ev.data)));
      } catch {
        return;
      }
      switch (msg.t) {
        case "ops":
          this.#applyStored(msg.ops, true);
          this.set((s) => ({ ...s, head: msg.head, lastSyncAt: Date.now(), status: "live" }));
          if (msg.more) ws.send(JSON.stringify({ t: "pull", from_seq: this.get().lastSeq }));
          break;
        case "pong":
          this.set((s) => ({ ...s, head: msg.head, lastSyncAt: Date.now() }));
          if (msg.head > this.get().lastSeq) ws.send(JSON.stringify({ t: "pull", from_seq: this.get().lastSeq }));
          break;
        case "revoked":
          this.set((s) => ({ ...s, status: "revoked" }));
          this.#stopped = true;
          this.onRevoked?.();
          break;
        case "ack":
        case "error":
          break;
      }
    };
    ws.onclose = (ev) => {
      if (this.#pingTimer) clearInterval(this.#pingTimer);
      this.#pingTimer = null;
      if (this.#ws === ws) this.#ws = null;
      if (this.#stopped) return;
      if (ev.code === 4001) {
        this.set((s) => ({ ...s, status: "revoked" }));
        this.onRevoked?.();
        return;
      }
      this.set((s) => ({ ...s, status: "offline" }));
      this.#scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows
    };
  }

  #scheduleReconnect(): void {
    if (this.#stopped || this.#reconnectTimer) return;
    const delay = backoffDelay(this.#attempt++);
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      void (async () => {
        try {
          await this.#pullAll();
          this.#connect();
        } catch {
          this.set((s) => ({ ...s, status: "offline" }));
          this.#scheduleReconnect();
        }
      })();
    }, delay);
  }

  /** Encrypt, apply locally and push one or more ops authored by this device. */
  async commit(ops: Op[]): Promise<void> {
    const vault = this.#vault;
    if (!vault || ops.length === 0) return;
    const made = ops.map((op) => vault.commit(op));
    this.#scheduleSnapshot();
    this.set((s) => ({ ...s, pendingPushes: s.pendingPushes + 1 }));
    try {
      const { results } = await api.vault.push({ ops: made.map((m) => m.encrypted) });
      const now = Date.now();
      const stored: StoredOp[] = made.map((m, i) => ({ ...m.encrypted, seq: results[i]?.seq ?? 0, created_at: now }));
      const { all, added } = mergeOps(this.#ops, stored);
      this.#ops = all;
      for (const op of added) this.#known.add(op.op_id);
      void cacheOps(this.#vaultId, added);
      const decoded: DecodedOp[] = made.map((m, i) => ({
        seq: results[i]?.seq ?? 0,
        op_id: m.encrypted.op_id,
        device_id: this.#deviceId,
        created_at: now,
        op: m.op,
      }));
      this.set((s) => ({
        ...s,
        lastSeq: Math.max(s.lastSeq, ...stored.map((o) => o.seq)),
        activity: [...decoded.reverse(), ...s.activity].slice(0, MAX_ACTIVITY),
        lastSyncAt: now,
      }));
    } finally {
      this.set((s) => ({ ...s, pendingPushes: Math.max(0, s.pendingPushes - 1) }));
    }
  }

  /** Force a pull (e.g. user clicked refresh). */
  async refresh(): Promise<void> {
    await this.#pullAll();
    if (!this.#ws) this.#connect();
  }
}

export const vaultStore = new VaultStore();
